import React, { useState } from 'react';
import './QueryPanel.css';
import { sapApi } from '../services/api';

const QueryPanel = ({ entity }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [executionTime, setExecutionTime] = useState(null);

  // Sample queries for quick access
  const sampleQueries = [
    {
      name: 'Chart of Accounts',
      query: 'SELECT TOP 100 AcctCode, AcctName, GroupMask, Levels FROM OACT WHERE Postable = \'Y\' ORDER BY AcctCode'
    },
    {
      name: 'Recent Journal Entries',
      query: 'SELECT TOP 50 TransId, Number, RefDate, Memo FROM OJDT ORDER BY RefDate DESC'
    },
    {
      name: 'Account Balances',
      query: 'SELECT TOP 100 t0.AcctCode, t0.AcctName, COALESCE(SUM(t1.Debit), 0) AS TotalDebit, COALESCE(SUM(t1.Credit), 0) AS TotalCredit FROM OACT t0 LEFT JOIN JDT1 t1 ON t0.AcctCode = t1.Account WHERE t0.Postable = \'Y\' GROUP BY t0.AcctCode, t0.AcctName ORDER BY t0.AcctCode'
    },
    {
      name: 'Business Partners',
      query: 'SELECT TOP 100 CardCode, CardName, CardType, Currency FROM OCRD ORDER BY CardName'
    },
    {
      name: 'Items Master Data',
      query: 'SELECT TOP 100 ItemCode, ItemName, InvntItem, OnHand, AvgPrice FROM OITM ORDER BY ItemCode'
    }
  ];

  const handleDownloadCsv = async () => {
    if (!query.trim()) {
      setError('Please enter a query');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const blob = await sapApi.executeQueryCsv(entity.name, query);

      // Create download link
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', `${entity.name}-tb-0104-3011-1812.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      console.log('✓ CSV downloaded successfully');
    } catch (err) {
      if (err.isApiError && err.data) {
        const errorMessage = err.data.error || err.message || 'CSV download failed';
        setError(errorMessage);
        console.error('❌ API Error:', err.data);
      } else {
        const errorMessage = err.message || 'CSV download failed';
        setError(errorMessage);
        console.error('❌ CSV download error:', err);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleExecuteQuery = async () => {
    if (!query.trim()) {
      setError('Please enter a query');
      return;
    }

    setLoading(true);
    setError(null);
    setResults(null);
    setExecutionTime(null);

    try {
      console.log('🔍 Executing query...');
      let data = await sapApi.executeQuery(entity.name, query);

      // Debug logging
      console.log('=== Query Response Debug ===');
      console.log('Response type:', typeof data);

      // Handle case where response might be a string (large JSON that wasn't parsed)
      if (typeof data === 'string') {
        console.log('Response is a string, parsing JSON...');
        try {
          data = JSON.parse(data);
          console.log('✓ Successfully parsed JSON string');
        } catch (parseError) {
          console.error('Failed to parse JSON:', parseError);
          throw new Error('Received invalid JSON from server');
        }
      }

      console.log('Full response:', data);
      console.log('Response keys:', data ? Object.keys(data) : 'null/undefined');
      console.log('success field:', data?.success);
      console.log('error field:', data?.error);
      console.log('columns field:', data?.columns);
      console.log('data field:', data?.data);
      console.log('===========================');

      // Check if response exists
      if (!data) {
        throw new Error('No response received from server');
      }

      // Check for error response
      if (data.success === false || data.error) {
        throw new Error(data.error || 'Query execution failed');
      }

      // Validate required fields
      if (!data.columns || !Array.isArray(data.columns)) {
        console.error('Invalid columns:', data.columns);
        throw new Error('Response missing valid columns array. Check console for details.');
      }

      if (!data.data || !Array.isArray(data.data)) {
        console.error('Invalid data:', data.data);
        throw new Error('Response missing valid data array. Check console for details.');
      }

      // All validations passed
      console.log('✓ Response validation passed');
      setResults(data);
      setExecutionTime(data.execution_time_ms);
    } catch (err) {
      // Handle structured API errors
      if (err.isApiError && err.data) {
        const errorMessage = err.data.error || err.message || 'Query execution failed';
        setError(errorMessage);
        console.error('❌ API Error:', err.data);
      } else {
        const errorMessage = err.message || 'An unknown error occurred';
        setError(errorMessage);
        console.error('❌ Query execution error:', err);
        console.error('Error message:', errorMessage);
      }
      setResults(null);
    } finally {
      setLoading(false);
    }
  };

  const loadSampleQuery = (sampleQuery) => {
    setQuery(sampleQuery);
    setResults(null);
    setError(null);
  };

  const clearQuery = () => {
    setQuery('');
    setResults(null);
    setError(null);
    setExecutionTime(null);
  };

  const exportToCSV = () => {
    if (!results || !results.data || results.data.length === 0) {
      return;
    }

    // Create CSV content
    const headers = results.columns.join(',');
    const rows = results.data.map(row =>
      results.columns.map(col => {
        const value = row[col];
        // Escape values containing commas or quotes
        if (value === null || value === undefined) return '';
        const strValue = String(value);
        if (strValue.includes(',') || strValue.includes('"') || strValue.includes('\n')) {
          return `"${strValue.replace(/"/g, '""')}"`;
        }
        return strValue;
      }).join(',')
    ).join('\n');

    const csv = `${headers}\n${rows}`;

    // Create download link
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${entity.name}-tb-0104-3011-1812.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="query-panel">
      <div className="query-panel-header">
        <h2>SQL Query Executor</h2>
        <p className="query-subtitle">Execute direct SQL queries on SAP database for {entity.finalyzer_company_code}</p>
      </div>

      <div className="query-section">
        <div className="sample-queries">
          <h3>Sample Queries</h3>
          <div className="sample-query-buttons">
            {sampleQueries.map((sample, index) => (
              <button
                key={index}
                className="sample-query-btn"
                onClick={() => loadSampleQuery(sample.query)}
                title={sample.query}
              >
                {sample.name}
              </button>
            ))}
          </div>
        </div>

        <div className="query-editor">
          <div className="query-editor-header">
            <label htmlFor="query-input">SQL Query</label>
            <div className="query-actions">
              <button onClick={clearQuery} className="btn-secondary" disabled={loading}>
                Clear
              </button>
              <button onClick={handleDownloadCsv} className="btn-secondary" disabled={loading || !query.trim()} title="Download query results as CSV">
                Download CSV
              </button>
              <button onClick={handleExecuteQuery} className="btn-primary" disabled={loading || !query.trim()}>
                {loading ? 'Executing...' : 'Execute Query'}
              </button>
            </div>
          </div>
          <textarea
            id="query-input"
            className="query-textarea"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Enter your SQL query here... (Only SELECT queries are allowed)"
            rows={8}
            disabled={loading}
          />
          <p className="query-note">
            Security Note: Only SELECT queries are allowed. Queries with INSERT, UPDATE, DELETE, DROP, etc. will be rejected.
          </p>
        </div>

        {error && (
          <div className="query-error">
            <strong>Error:</strong> {error}
          </div>
        )}

        {loading && (
          <div className="query-loading">
            <div className="spinner"></div>
            <p>Executing query...</p>
          </div>
        )}

        {results && !loading && results.success !== false && results.columns && results.data && (
          <div className="query-results">
            <div className="results-header">
              <div className="results-info">
                <h3>Query Results</h3>
                <div className="results-meta">
                  <span className="meta-item">
                    <strong>Rows:</strong> {results.row_count || 0}
                  </span>
                  <span className="meta-item">
                    <strong>Columns:</strong> {results.column_count || 0}
                  </span>
                  {executionTime && (
                    <span className="meta-item">
                      <strong>Execution Time:</strong> {executionTime.toFixed(2)}ms
                    </span>
                  )}
                </div>
              </div>
              <button onClick={exportToCSV} className="btn-export">
                Export to CSV
              </button>
            </div>

            {results.row_count === 0 ? (
              <div className="no-results">
                <p>Query executed successfully but returned no results.</p>
              </div>
            ) : (
              <div className="results-table-container">
                <table className="results-table">
                  <thead>
                    <tr>
                      {results.columns.map((col, index) => (
                        <th key={index}>{col}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {results.data.map((row, rowIndex) => (
                      <tr key={rowIndex}>
                        {results.columns.map((col, colIndex) => (
                          <td key={colIndex}>
                            {row[col] !== null && row[col] !== undefined
                              ? String(row[col])
                              : <span className="null-value">NULL</span>
                            }
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default QueryPanel;
